// Undo for actions that already reached the server (dismiss, snooze, queued instructions, an
// approval inside its undo window, a reversible Gmail change). Each entry shows a toast with an
// Undo button for a few seconds (closable; the toast is a notice, not the window); `u` runs the
// newest entry that is still open. The stack itself is pure and testable; `registerUndo` adds
// timers and toasts.
import { useSyncExternalStore } from "react";
import { toast } from "sonner";
import { isApiError } from "./api";

export const LOCAL_UNDO_MS = 30_000;
/** How long an undo toast stays on screen. The window itself (and `u`) can run on longer. */
export const UNDO_TOAST_MS = 5_000;

export interface UndoEntry {
  id: string;
  message: string;
  /** Epoch ms after which Undo is no longer offered. */
  expiresAt: number;
  undo: () => Promise<unknown> | unknown;
  /** Shown after a successful undo (computed from what the undo returned; null shows nothing). */
  doneMessage: string | ((result: unknown) => string | null);
}

export class UndoStack {
  private entries: UndoEntry[] = [];

  push(entry: UndoEntry): void {
    this.entries = [...this.entries.filter((e) => e.id !== entry.id), entry];
  }

  /** Newest entry still inside its window. */
  latest(now: number): UndoEntry | null {
    this.prune(now);
    return this.entries[this.entries.length - 1] ?? null;
  }

  /** Remove and return an entry if it is still open (an entry can only be undone once). */
  take(id: string, now: number): UndoEntry | null {
    this.prune(now);
    const hit = this.entries.find((e) => e.id === id) ?? null;
    if (hit) this.entries = this.entries.filter((e) => e.id !== id);
    return hit;
  }

  remove(id: string): void {
    this.entries = this.entries.filter((e) => e.id !== id);
  }

  prune(now: number): void {
    this.entries = this.entries.filter((e) => e.expiresAt > now);
  }

  get size(): number {
    return this.entries.length;
  }
}

/** Remaining ms of an undo window that ends at `until` (ISO), or null when there is none. */
export function undoWindowMs(until: string | null | undefined, now: number): number | null {
  if (!until) return null;
  const end = new Date(until).getTime();
  if (Number.isNaN(end)) return null;
  const left = end - now;
  return left > 0 ? left : null;
}

const stack = new UndoStack();
const listeners = new Set<() => void>();
let available = false;
let counter = 0;

function emit() {
  available = stack.latest(Date.now()) !== null;
  for (const l of listeners) l();
}

export interface RegisterUndoOptions<T> {
  message: string;
  /** Window length; defaults to LOCAL_UNDO_MS. */
  durationMs?: number;
  /**
   * How long the toast stays, capped at the window; defaults to UNDO_TOAST_MS. A send that is
   * still waiting to go passes its whole window: the toast is the way to stop it.
   */
  toastMs?: number;
  undo: () => Promise<T> | T;
  /**
   * Shown after a successful undo. A function when the undo can end more than one way; it returns
   * null when the undo already announced itself (for instance with an undo toast of its own).
   */
  doneMessage: string | ((result: T) => string | null);
  /** Shown when the server refuses the undo (e.g. the work was already claimed). */
  tooLateMessage?: string;
}

async function runEntry(entry: UndoEntry, tooLate: string) {
  toast.dismiss(entry.id);
  emit();
  try {
    const result = await entry.undo();
    const done =
      typeof entry.doneMessage === "string" ? entry.doneMessage : entry.doneMessage(result);
    if (done) toast(done);
  } catch (error) {
    toast(isApiError(error) && error.status === 409 ? tooLate : "Couldn't undo that. Try again.");
  }
}

export function registerUndo<T>(options: RegisterUndoOptions<T>): string {
  const id = `undo-${++counter}`;
  const duration = options.durationMs ?? LOCAL_UNDO_MS;
  const tooLate = options.tooLateMessage ?? "Too late to undo: it's already under way.";
  const { doneMessage } = options;
  const entry: UndoEntry = {
    id,
    message: options.message,
    expiresAt: Date.now() + duration,
    undo: options.undo,
    // The entry hands `doneMessage` exactly what this entry's `undo` resolved to.
    doneMessage:
      typeof doneMessage === "string" ? doneMessage : (result) => doneMessage(result as T),
  };
  stack.push(entry);
  emit();
  setTimeout(() => {
    stack.prune(Date.now());
    emit();
  }, duration + 50);
  toast(options.message, {
    id,
    duration: Math.min(options.toastMs ?? UNDO_TOAST_MS, duration),
    action: {
      label: "Undo",
      onClick: () => {
        const taken = stack.take(id, Date.now());
        emit();
        if (taken) void runEntry(taken, tooLate);
      },
    },
  });
  return id;
}

/** Drop an entry whose action was undone another way (its toast goes too). */
export function dismissUndo(id: string): void {
  stack.remove(id);
  toast.dismiss(id);
  emit();
}

/** `u`: undo the newest open entry. Returns false when there is nothing to undo. */
export function undoLatest(): boolean {
  const latest = stack.latest(Date.now());
  if (!latest) return false;
  const taken = stack.take(latest.id, Date.now());
  if (taken) void runEntry(taken, "Too late to undo: it's already under way.");
  emit();
  return true;
}

/** True while at least one undo window is open (enables the `u` shortcut). */
export function useUndoAvailable(): boolean {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => available,
    () => false,
  );
}
